/**
 * @lines98/client/lib/gnome-frame-aspect — ЕДИНАЯ ширина рамки гнома: максимальный аспект
 * (ширина/высота) по натуральным размерам всех 7 full-PNG гномов.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-06-14 «все карточки одной ширины БЕЗ
 * растягивания/ужатия гномов»). Карточка гнома (`components/gnome-card`) ставит рамку-героя
 * через CSS `aspect-ratio: var(--gnc-frame-aspect, ...)`. Чтобы ВСЕ гномы имели рамку
 * ОДНОЙ ширины (= по самому широкому), `--gnc-frame-aspect` = max(w/h) по всем 7 PNG; узкий
 * гном стоит внутри (object-fit:contain), не деформируется.
 *
 * Экран «Гномы» (`gnomes-screen`) считает этот max из СВОИХ 7 отрисованных карточек (все
 * присутствуют в DOM). Но МОДАЛЬНЫЙ ОВЕРЛЕЙ открытия помощника (`lib/helper-unlock-overlay`)
 * показывает ОДНУ карточку → не имеет всех 7 imgs под рукой и без этого значения рисует
 * гнома в ЕГО собственном (более узком) аспекте → рамка-герой УЖЕ, чем на «Помощниках»
 * (owner-playtest 2026-06-20: «карточка не того же размера»). Этот модуль даёт оверлею тот
 * же max-аспект, что у сетки «Помощники» → оверлей-карточка пиксель-в-пиксель = grid-карточке.
 *
 * Источник URL'ов — `components/meta-icons.gnomeFullImageUrls()` (single source списка PNG);
 * сам модуль НЕ перечисляет ассеты. Результат кешируется (промис) — загрузка/декод 7 PNG раз
 * на сессию (PNG и так прогреты `lib/asset-preload`).
 *
 * SCOPE: загрузить 7 full-PNG, вернуть max(naturalWidth/naturalHeight). NOT IN SCOPE: сам
 * рендер карточки (gnome-card), раскладка оверлея (helper-unlock-overlay.css).
 *
 * Cross-references:
 *   - components/meta-icons.ts (gnomeFullImageUrls — single source URL-списка).
 *   - components/gnome-card.css (--gnc-frame-aspect → aspect-ratio рамки-героя).
 *   - lib/helper-unlock-overlay.ts (потребитель: ставит --gnc-frame-aspect на оверлей-карточку).
 *   - screens/gnomes-screen.ts (считает тот же max из своих 7 отрисованных карточек).
 */

import { gnomeFullImageUrls } from '../components/meta-icons.js';
import { getDecodedMetaAsset } from './asset-preload.js';

/** Кеш промиса (вычисляем один раз на сессию). */
let cached: Promise<number> | null = null;

/** Безопасный фолбэк-аспект до загрузки PNG (узкий-высокий гном ≈ 1/2) — = CSS-fallback. */
const FALLBACK_ASPECT = 1 / 2;

/**
 * Загрузить натуральные размеры одного PNG и вернуть его аспект (w/h). Битый/незагрузившийся
 * → 0 (исключается из max). Без DOM-вставки — `new Image()` декодирует вне дерева.
 */
function loadAspect(url: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 0);
    };
    img.onerror = () => resolve(0);
    img.src = url;
  });
}

/**
 * max(width/height) по всем 7 full-PNG гномов = ЕДИНАЯ ширина рамки (owner-directive
 * 2026-06-14). Кешируется. Если ни один PNG не загрузился — фолбэк 1/2 (= CSS-fallback,
 * карточка не «схлопывается»). Чистый от DOM (тестируемо/переиспользуемо).
 */
export function loadMaxGnomeFrameAspect(): Promise<number> {
  if (cached) return cached;
  cached = Promise.all(gnomeFullImageUrls().map(loadAspect)).then((aspects) => {
    const max = Math.max(0, ...aspects);
    return max > 0 ? max : FALLBACK_ASPECT;
  });
  return cached;
}

/**
 * СИНХРОННЫЙ max-аспект ИЗ УЖЕ ДЕКОДИРОВАННЫХ спрайтов (boot-prewarm `preloadMetaAssets`),
 * или `null`, если ни один full-PNG ещё не декодирован. Зачем (feat/fe-hydration п.5 /
 * AC-7/AC-9): экран «Помощники» ставит `--gnc-frame-aspect` на список ДО первого пэйнта
 * карточек, чтобы ширина рамки-героя НЕ прыгала (zero-CLS). На тёплом пути (boot прогрел
 * ассеты) значение готово синхронно → ставим до append карточек. Источник natural-размеров —
 * ровно те же декодированные `<img>`, что рисует карточка (`gnomeFullGlyph` → `GNOME_FULL_IMG`,
 * единый список `gnomeFullImageUrls`, R-3/R-4) → измеренный (NOT хардкод, AC-10). Требует
 * ВСЕХ 7 декодированными (иначе max неполон → ширина прыгнула бы при доезде последнего):
 * если хоть один не готов — `null` (caller дождётся `loadMaxGnomeFrameAspect`).
 */
export function peekMaxGnomeFrameAspect(): number | null {
  let max = 0;
  for (const url of gnomeFullImageUrls()) {
    const img = getDecodedMetaAsset(url);
    if (!img || img.naturalWidth <= 0 || img.naturalHeight <= 0) return null; // не все готовы
    max = Math.max(max, img.naturalWidth / img.naturalHeight);
  }
  return max > 0 ? max : null;
}

/**
 * Поставить `--gnc-frame-aspect` на элемент = max-аспект всех гномов (как `.gnomes-list` на
 * «Помощниках») → рамка-герой карточки внутри `el` той же ширины, что в сетке «Помощники».
 * Fire-and-forget (await внутри); если элемент уже снят со страницы — no-op безвреден.
 *
 * @param el — контейнер оверлей-карточки (CSS var каскадит на `.gnome-card-hero` внутри).
 */
export async function applyMaxGnomeFrameAspect(el: HTMLElement): Promise<void> {
  const max = await loadMaxGnomeFrameAspect();
  el.style.setProperty('--gnc-frame-aspect', String(max));
}
